import { create } from 'zustand';
import { db } from '../lib/database';
import { Employee } from '../types';

interface EmployeeState {
  employees: Employee[];
  isLoading: boolean;
  error: string | null;
  fetchEmployees: () => Promise<void>;
  getEmployeeById: (id: string) => Employee | undefined;
  getEmployeesByIds: (ids: string[]) => Employee[];
  getWorkers: () => Employee[];
  updateEmployee: (id: string, data: FormData) => Promise<void>;
  toggleEmployeeActive: (id: string, isActive: boolean, userRole: string) => Promise<void>;
}

export const useEmployeeStore = create<EmployeeState>((set, get) => ({
  employees: [],
  isLoading: false,
  error: null,
  
  fetchEmployees: async () => {
    set({ isLoading: true, error: null });
    try {
      const data = await db.getEmployees();

      const employees: Employee[] = data.map((emp: any) => ({
        id: emp.id,
        name: emp.name,
        email: emp.email,
        position: emp.position,
        role: emp.role as any,
        profile_image: emp.profile_image || null,
        phone: emp.phone || null,
        password_changed: emp.password_changed,
        is_active: emp.is_active !== false
      }));

      set({ employees, isLoading: false });
    } catch (error) {
      console.error('Error fetching employees:', error);
      set({ error: "Failed to fetch employees", isLoading: false });
    }
  },
  
  getEmployeeById: (id: string) => {
    return get().employees.find(employee => employee.id === id);
  },
  
  getEmployeesByIds: (ids: string[]) => {
    return get().employees.filter(employee => ids.includes(employee.id));
  },
  
  getWorkers: () => {
    return get().employees.filter(employee => employee.role !== 'admin' && employee.is_active !== false);
  },

  updateEmployee: async (id: string, data: FormData) => {
    const userRole = data.get('_userRole') as string || 'master';
    data.delete('_userRole');
    const res = await fetch(`/api/employees/${id}`, {
      method: 'PUT',
      headers: { 'user-role': userRole },
      body: data,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Greška pri ažuriranju');
    }
    const updated = await res.json();
    set(state => ({
      employees: state.employees.map(e =>
        e.id === String(updated.id)
          ? { ...e, name: updated.name, email: updated.email, position: updated.position, role: updated.role, phone: updated.phone || null, profile_image: updated.profile_image || null, is_active: updated.is_active !== false }
          : e
      )
    }));
  },

  toggleEmployeeActive: async (id: string, isActive: boolean, userRole: string) => {
    const res = await fetch(`/api/employees/${id}/active`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'user-role': userRole },
      body: JSON.stringify({ is_active: isActive }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Greška pri promeni statusa');
    }
    set(state => ({
      employees: state.employees.map(e =>
        e.id === id ? { ...e, is_active: isActive } : e
      )
    }));
  },
}));